<template>
  <!-- Page length options -->
  <div class="card">
    <div>
      <div
        v-show="onload"
        class="content bg-white"
      >
        <PlaceholderContent is-load="true" />
      </div>
      <div v-if="!onload && !hasPermission">
        <AccessDenied />
      </div>
      <div v-show="!onload && hasPermission">
        <div>
          <div
            class="card-header bg-white header-elements-inline"
          >
            <h5 class="card-title">
              {{ $t("Product Sales Report By Category") }}
              <span>
                <a
                  style="color: #706f72"
                  data-trigger="hover"
                  data-popup="popover-suggestion"
                  data-placement="right"
                  :title="$t('_ReportChannel.Suggestion')"
                  :data-content="
                    $t(
                      'A comprehensive document that details the sales performance of products grouped by their respective categories over a specific period.'
                    )
                  "
                >
                  <i class="fas fa-info-circle" />
                </a>
              </span>
            </h5>
          </div>
          <div
            class="card-body"
          >
            <div
              class="card-filter d-flex flex-wrap align-items-center bg-light border-bottom p-3 mt-n3 mx-n3 mb-3"
            >
              <!-- Date filter -->
              <div class="input-group flex-wrap w-100">
                <span class="col-form-label mr-3">{{
                  $t("Order Date")
                }}</span>
                <div class="input-group w-auto">
                  <span class="input-group-prepend">
                    <span class="input-group-text"><i class="fas fa-calendar-alt" /></span>
                  </span>
                  <DateRangePicker
                    style="min-width: 175px"
                    opens="right"
                    :ranges="ranges"
                    v-model="dateRange"
                    :locale-data="localedata"
                    :control-container-class="'form-control rounded-left-0 rounded-right-0 border-right-0'"
                    @update="applyDatePicker"
                  >
                    <div
                      slot="input"
                      v-html="showTextPicker"
                    />
                    <div
                      slot="footer"
                      slot-scope="data"
                      class="p-2 drp-buttons"
                    >
                      <button
                        @click="cancelDateRange()"
                        v-if="!data.in_selection"
                        type="button"
                        class="applyBtn btn btn-sm btn-secondary"
                      >
                        {{ $t("Cancel") }}
                      </button>
                      <button
                        @click="data.clickApply"
                        v-if="!data.in_selection"
                        type="button"
                        class="applyBtn btn btn-sm btn-primary"
                      >
                        {{ $t("Apply") }}
                      </button>
                    </div>
                  </DateRangePicker>
                  <button
                    @click="openAdvanceFilter()"
                    type="button"
                    class="btn btn-outline-normal rounded-left-0 d-flex align-items-center"
                    data-toggle="modal"
                    data-target="#product_more_filter"
                  >
                    <i class="fas fa-sliders-v mr-2" />
                    <span>{{ $t("_ReportChannel.More Filters") }}</span>
                    <span
                      v-if="countFilter"
                      class="badge badge-secondary ml-2"
                    >{{
                      countFilter
                    }}</span>
                  </button>
                  <span
                    v-if="showTotalFilter"
                    class="ml-2 text-blue-800"
                    style="padding-top: 8px"
                  >
                    {{ $t("Filters Results") }}:
                    {{ totalFilter | formatNumberNotZero }}
                  </span>
                </div>
                <!-- Set PerPage -->
                <div class="d-flex align-items-center dataTables_length mb-0 ml-auto">
                  <div class="dataTables_length ml-auto mb-0">
                    <PerPage @per-page="setPerPage" />
                  </div>
                </div>
              </div>
              <!-- \Date filter -->
            </div>

            <OverviewChart :overview-data="charts" />

            <div
              class="card"
            >
              <div
                :class="['table-responsive mt-0 data-nowrap table-hover table-order']"
                id="table-order"
              >
                <div class="vuetable-wrapper datatable-scroll">
                  <Vuetable
                    :class="[loadingTable, 'thead-bg-fafafa']"
                    ref="vuetable"
                    :no-data-template="$t('No Data Available')"
                    :fields="table.fields"
                    data-path="data"
                    pagination-path=""
                    :api-mode="false"
                    :data-manager="dataManager"
                    @vuetable:loaded="vuetableLoaded"
                    @vuetable:loading="vuetableLoading"
                    @vuetable:initialized="onInitialized"
                    @vuetable:pagination-data="onPaginationData"
                    :sort-order="sort"
                  >
                    <div
                      slot="ranking"
                      slot-scope="props"
                    >
                      <i
                        v-if="props.rowData.i_order == 1"
                        class="fas fa-trophy-alt mr-2 text-orange"
                      />
                      <i
                        v-else-if="props.rowData.i_order == 2"
                        class="fas fa-trophy-alt mr-2 text-grey-300"
                      />
                      <i
                        v-else-if="props.rowData.i_order == 3"
                        class="fas fa-trophy-alt mr-2 text-orange-800"
                      />
                      <i
                        v-else
                        class="fas fa-trophy-alt mr-2 opacity-0"
                        style="color: transparent;"
                      />
                      {{ $t('No.') }} {{ props.rowData.i_order }}
                    </div>
                    <div
                      slot="image"
                      slot-scope="props"
                    >
                      <ProductImageComponent
                        :product-id="props.rowData.id"
                        :product-image-url="props.rowData.images"
                        :module-type="'category'"
                      />
                    </div>

                    <div
                      slot="name"
                      slot-scope="props"
                    >
                      <div
                        class="items d-flex align-items-center "
                      >
                        <div class="items-right">
                          <div class="font-weight-semibold item-name text-primary">
                            <span>
                              <router-link
                                :to="{ name: 't.a.category', query: {categoryId: props.rowData.id}}"
                                target="_blank"
                              >
                                <ProductNameComponent
                                  :product-name="props.rowData.name"
                                  :show-line-second="false"
                                />
                              </router-link>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <template slot="tableFooter">
                      <tr style="background:#FAFAFA">
                        <th
                          v-for="(field, indexField) in dataFooterFields"
                          :class="field.dataClass"
                          :key="indexField + '_' + field.visible"
                        >
                          <template v-if="field.name == 'ranking'">
                            {{ $t('Total Amount') }}
                          </template>
                          <template v-else-if="field.name == 'quantity'">
                            {{ totalCountQTY | formatNumberDigit }}
                          </template>
                          <template v-else-if="field.name == 'price'">
                            {{ totalCountAmount | formatNumber }}
                          </template>
                          <template v-else-if="field.name == 'discount'">
                            {{ totalCountDiscount | formatNumber }}
                          </template>
                          <template v-else-if="field.name == 'total'">
                            {{ totalCountTotalSale | formatNumber }}
                          </template>
                        </th>
                      </tr>
                    </template>
                  </Vuetable>
                </div>
              </div>
              <div class="datatable-footer">
                <VuetablePaginationInfo
                  class="dataTables_info ml-3"
                  ref="paginationInfo"
                  :no-data-template="$t('No relevant data')"
                  :info-template="$t('Displaying {from} to {to} of {total} items')"
                />
                <div class="dataTables_paginate paging_simple_numbers mb-0 mr-2">
                  <VuetablePagination
                    ref="pagination"
                    pagination-path="pagination"
                    @vuetable-pagination:change-page="onChangePage"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <MoreFilter
      v-if="isAdvanceFilter"
      ref="moreFilter"
      :status-list="statusList"
      @onSubmit="submitMoreFilter"
      :product-type="productType"
    />
  </div>
</template>

<script>
import Api from "@/services/endpoint/tenant.service";
import MoreFilter from "./ProductCategoryMoreFilter.vue";
import DateRangePicker from "vue2-daterange-picker";
import PerPage from "../PerPage.vue";
import Select2 from "v-select2-component";
import Vuetable from "vuetable-2";
import VuetablePagination from "vuetable-2/src/components/VuetablePagination";
import VuetablePaginationInfo from "@/plugins/vuetable-2/VuetablePaginationInfo";
import TableFields from "../assets/js/FieldsProductCategory.js";
import { showTextPicker } from "../assets/directives/Vuetable.js";
import moment from "moment";
import { convertTimezone } from "@/common/tenancy/convertTimezone";
import numeral from "numeral";
import PlaceholderContent from "@/plugins/PlaceholderContent.vue";
import AccessDenied from "@/components/tenancy/admin/error-page/templates/AccessDenied.vue";
import OverviewChart from "./chart/category/OverviewChart.vue";
import ProductImageComponent from '@/components/tenancy/center/ProductImageComponent.vue';
import ProductNameComponent from '@/components/tenancy/center/ProductNameComponent.vue';

export default {
  name: "ReportCategoryComponent",
  components: {
    MoreFilter,
    DateRangePicker,
    PerPage,
    // Select2,
    Vuetable,
    VuetablePagination,
    VuetablePaginationInfo,
    PlaceholderContent,
    AccessDenied,
    OverviewChart,
    ProductImageComponent,
    ProductNameComponent
  },
  data() {
    return {
      checkedCount: 0,
      table: {
        fields: TableFields,
      },
      defaultStartDate: moment().subtract(1, "months").format("YYYY-MM-DD 17:00:00"),
      defaultEndDate: moment().format("YYYY-MM-DD 16:59:59"),
      paramsQuery: {
        start: moment().subtract(1, "months").format("YYYY-MM-DD 17:00:00"),
        end: moment().format("YYYY-MM-DD 16:59:59"),
        limit: 10,
        page: 1,
        sort: "",
        product_type: null,
        order_status: null,
        category_ids: [],
      },
      dateRange: {
        startDate: this.defaultStartDate,
        endDate: this.defaultEndDate,
      },
      // amount:{
      //     countShipment: 0,
      //     totalShipping: 0,
      // },
      vuetableFields: false,
      isAdvanceFilter: false,
      countFilter: 0,
      totalFilter: 0,
      // shipmentStatus: [],
      // shippingMethod: [],
      showTotalFilter: false,
      dataFooterFields: [],
      dataTable: [],
      sort: [],
      onload: true,
      hasPermission: true,
      ranges: this.$storeIndex.ranges(),
      localedata: {
        format: "DD-MM-YYYY",
        applyLabel: this.$t("Apply"),
        cancelLabel: this.$t("Cancel"),
        clearable: true,
        monthNames: this.$storeIndex.monthNames(),
        daysOfWeek: this.$storeIndex.daysOfWeek(),
      },
      dateTxtFilter: null,
      loadingTable: null,
      sales_order_summary: {
        orders_category: 0,
        sold_qty: 0,
        total_sales_product: 0,
      },
      charts: {
        total_sales_product: [],
        sold_qty: [],
        orders_category: [],
      },
      // i: 0,
      // tHeaderShipping: null,
      // filterValShipping: null,
      // list: null,
      // filename: '',
      // autoWidth: true,
      // bookType: 'xlsx',
      // shipmentStatusSetting: {
      //     minimumResultsForSearch: Infinity,
      //     placeholder: this.$t("Shipping Status") + "..",
      // },
      salesOrderSummary: {
        orders_category: 0,
        sold_qty: 0,
        total_sales_product: 0,
      },
      statusList: [],
      orderStatusList: [
        {
          id: "pending",
          icon: '<i class="fa-solid fa-clock"></i>',
          name: "_Shipment.Pending",
        },
        {
          id: "processing",
          icon: '<i class="fas fa-sync-alt"></i>',
          name: "Processing",
        },
        {
          id: "completed",
          icon: '<i class="fas fa-check-circle"></i>',
          name: "Completed",
        },
        {
          id: "returned",
          icon: '<i class="fa-solid fa-arrow-rotate-left"></i>',
          name: "_Order.Returned",
        },
        {
          id: "canceled",
          icon: '<i class="fas fa-times-square"></i>',
          name: "Cancel",
        },
      ],
      filterPaymentMethod: null,
      productType:[],
      productTypeList: [
        {
          id: "all",
          name: "All",
        },
        {
          id: "simple",
          name: "Simple Product",
        },
        {
          id: "set",
          name: "Product Set",
        }
      ],
      // loading: "",
      dataCategory: "",
      categoryId: 0,
    };
  },
  created() {
    // this.tHeaderShipping = tHeaderShipping()
    // this.filterValShipping = filterValShipping()
  },
  async mounted() {
    this.dateRange.startDate = this.paramsQuery.start = this.$route.query.start_date
      ? this.$route.query.start_date
      : this.defaultStartDate;
    this.dateRange.endDate = this.paramsQuery.end = this.$route.query.end_date
      ? this.$route.query.end_date
      : this.defaultEndDate;
    // this.onload = true;
  },
  computed: {
    showTextPicker: function () {
      if (
        Object.keys(this.dateRange).includes("startDate") &&
        Object.keys(this.dateRange).includes("endDate") &&
        this.dateRange.startDate != null &&
        this.dateRange.endDate != null
      ) {
        this.dateRange.startDate = convertTimezone.utcToLocal(this.dateRange.startDate);
        this.dateRange.endDate = convertTimezone.utcToLocal(this.dateRange.endDate);
        this.dateTxtFilter = showTextPicker(this.dateRange);
        return this.dateTxtFilter;
      } else {
        return showTextPicker(this.paramsQuery);
      }
    },
    totalCountOrder() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.orders) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountQTY() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.quantity) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountAmount() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.price) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountDiscount() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.discount) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    totalCountTotalSale() {
      let dataTable = this.dataTable.data;
      if (dataTable && dataTable.length > 0) {
        let totalCount = dataTable.map((item) => { return parseFloat(item.total) });
        return totalCount.reduce((total, item) => total + item);
      }else {
        return 0
      }
    },
    footerTableFields() {
      if (this.vuetableFields !== false) {
        return this.$refs.vuetable.tableFields;
      } else {
        return [];
      }
    },
  },
  methods: {
    dataManager(sortOrder, pagination) {
      if (sortOrder.length > 0) {
        this.paramsQuery.sort = sortOrder
          .map((item) => `${item.sortField}|${item.direction}`)
          .join(",");
      }
      this.setParams();
    },
    onPaginationData(tablePagination) {
      this.$refs.paginationInfo.setPaginationData(tablePagination);
      this.$refs.pagination.setPaginationData(tablePagination);
      // console.log(tablePagination,'tablePagination');
    },
    onInitialized(fields) {
      this.vuetableFields = fields.filter((field) => field.togglable);
    },
    onChangePage(page) {
      this.$refs.vuetable.changePage(page);
    },
    search() {
      this.setParams();
    },
    setPerPage(perPage) { 
      this.paramsQuery.page = 1;
      this.$refs.vuetable.currentPage = 1;
      this.paramsQuery.limit = perPage;
      this.setParams();
    },
    openAdvanceFilter() {
      this.isAdvanceFilter = true;
      this.$nextTick(() => {
        this.isAdvanceFilter = true;
      });
    },
    vuetableLoaded() {
      this.onload = false;
      // this.loading = "";
    },
    vuetableLoading() {
      // this.loading = "loading";
    },
    cancelDateRange() {
      this.paramsQuery.start = this.defaultStartDate;
      this.paramsQuery.end = this.defaultEndDate;
      $(".daterangepicker").remove();
      this.setParams();
    },
    applyDatePicker(dateRange) {
      let startDate = this.paramsQuery.start
        ? moment(dateRange.startDate).format("YYYY-MM-DD")
        : null;
      let endDate = this.paramsQuery.end
        ? moment(dateRange.endDate).format("YYYY-MM-DD")
        : null;
      var afterStartDate = startDate + " 00:00:00";
      var afterEndDate = endDate + " 23:59:59";
      this.paramsQuery.start = convertTimezone.localToUtc(afterStartDate);
      this.paramsQuery.end = convertTimezone.localToUtc(afterEndDate);
      // this.paramsQuery.start = moment(afterStartDate).format("YYYY-MM-DD HH:mm:ss");
      // this.paramsQuery.end = moment(afterEndDate).format("YYYY-MM-DD HH:mm:ss");
      this.setParams();
    },
    async setParams() {
      let self = this;
      try {
        this.onload = true;
        // this.loading = "loading";
        this.paramsQuery.page = this.$refs.vuetable.currentPage;

        let queryString = "";
        if (this.paramsQuery) {
          queryString += $.param(this.paramsQuery);
        }

        let response = await Api.ReportV2.getProductSaleByCategoryReport(queryString);

        if(response.status != 200 && response.status != 201){
          throw response
        }

        let totalSalesProduct = [];
        let numberOfOrders = [];
        let totalSaleCategory = [];
        this.statusList = [];
        this.productType = [];
        let reportData = null;
        let reportDataTable = [];

        reportData = response.data;
        if (response.data && response.data.data && response.data.data.length) {
            response.data.data.map((v, i) => {
              reportDataTable.push({
                ...v,
                i_order: response.data.meta.from + i,
              })
            });
            response.data.data = reportDataTable;
          this.dataTable = response.data;
        } else {
          this.dataTable = [];
        }

        if (response.data && response.data.reports) {
          if (response.data.reports.charts && response.data.reports.charts.top_sales_product && response.data.reports.charts.top_sales_product.length) {
            response.data.reports.charts.top_sales_product.map(el => {
              totalSalesProduct.push({
                name: el.name,
                value: el.orders || 0,
              })
            });
            this.charts.total_sales_product = totalSalesProduct.sort((a, b) => b.value - a.value);
          } else {
            this.charts.total_sales_product = totalSalesProduct;
          }

          if (response.data.reports.charts && response.data.reports.charts.top_quantity_category && response.data.reports.charts.top_quantity_category.length) {
            response.data.reports.charts.top_quantity_category.map(el => {
              numberOfOrders.push({
                name: el.name,
                value: el.orders || 0,
              })
            });
            this.charts.sold_qty = numberOfOrders.sort((a, b) => b.value - a.value);
          } else {
            this.charts.sold_qty = numberOfOrders;
          }

          if (response.data.reports.charts && response.data.reports.charts.top_total_sales_category && response.data.reports.charts.top_total_sales_category.length) {
            response.data.reports.charts.top_total_sales_category.map(el => {
              totalSaleCategory.push({
                name: el.name,
                value: el.orders || 0,
              })
            });
            this.charts.orders_category = totalSaleCategory.sort((a, b) => b.value - a.value);
          } else {
            this.charts.orders_category = totalSaleCategory;
          }
        }

        if (response.data && response.data.filter && response.data.filter.order_status.length) {
          response.data.filter.order_status.map(status => {
            const findStatus = this.orderStatusList.find(p => p.id == status);
            if (findStatus && findStatus.id) {
              this.statusList.push(findStatus);
            }
          })
        }

        if (response.data && response.data.filter && response.data.filter.product_type.length) {
          response.data.filter.product_type.map(status => {
            const findProductType = this.productTypeList.find(p => p.id == status);
            if (findProductType && findProductType.id) {
              this.productType.push(findProductType);
            }
          })
        }

        let data = this.$storeIndex.mutations.vueTableFormatJson(response);
        this.$refs.vuetable.setData(data);
        this.onload = false;

      } catch (error) {
        // this.$router.push({ name: "t.a.home" });
        console.log(error,'error');
        this.onload = false;
        if (error.status === 403) {
          if (error.data.message.indexOf("You can not active plan feature") != -1) {
            this.planExpired = true;
          } else {
            this.hasPermission = false;
          }
        } else if (error.status === 500) {
          this.hasPermission = false;
        }

        this.dataTable = [];
        this.$refs.vuetable.setData(this.dataTable);
        this.salesOrderSummary = {
          orders: 0,
          sold_qty: 0,
          total_sales: 0,
        },
        this.charts = {
          total_sales: [],
          sold_qty: [],
          orders: [],
        };
      }
    },
    setQueryString(obj) {
      this.$router
        .push({
          query: Object.assign({}, this.$route.query, obj),
        })
        .catch(() => {});
    },
    submitMoreFilter(dataFilter) {
      this.paramsQuery.order_status = dataFilter.order_status;
      this.paramsQuery.product_type = dataFilter.product_type;
      this.paramsQuery.category_ids = Array.isArray(dataFilter.category_ids) ? dataFilter.category_ids.filter(Boolean) : [dataFilter.category_ids];


      let countOrderStatus = dataFilter.order_status && dataFilter.order_status.length > 0 ? 1 : 0;
      let countProductType = dataFilter.product_type && dataFilter.product_type.length > 0 ? 1 : 0;
      let countCategoryId = dataFilter.category_ids && dataFilter.category_ids.length > 0 ? 1 : 0;
      this.countFilter = countOrderStatus + countProductType + countCategoryId;

      this.setParams();
    },
    // changeShipmentStatusEvent(val) {
    //     this.setParams(1);
    // },
    // formatJson(filterValShipping, jsonData) {
    //     return jsonData.map(v => filterValShipping.map(j => {
    //         const jArr = j.split('.');
    //         if( jArr.length > 1){
    //             return v[jArr[0]][[jArr[1]]];
    //         }

    //         if (j === 'timestamp') {
    //             return parseTime(v[j])
    //         }else {
    //             return v[j]
    //         }
    //     }))
    // },
    convertTimezone(date) {
      return date != null && new Date(date) instanceof Date && !isNaN(new Date(date))
        ? convertTimezone.utcToLocal(date)
        : "-";
    },
  },
  watch: {
    footerTableFields: {
      handler: function (nVal, oVal) {
        let newFooterTableArray = [];
        nVal.forEach((element) => {
          if (element.visible === true) {
            newFooterTableArray.push(element);
          }
        });
        this.$nextTick(() => {
          this.dataFooterFields = newFooterTableArray;
        });
      },
      deep: true,
    },
    onload: function(nval) {
      _componentPopoverCustomHeaderColor();
    },
  },
  filters: {
    formatNumber: function (value) {
      return numeral(value).format("0,0.00");
    },
    formatNumberDigit: function (value) {
      return numeral(value).format("0,0");
    },
  },
};
var _componentPopoverCustomHeaderColor = function() {
  $('[data-popup=popover-suggestion]').popover({
    template: '<div class="popover border-light"><div class="arrow"></div><p class="popover-header bg-light font-weight-bold"></p><div class="popover-body"></div></div>'
  });
};
</script>

<style scoped>
.input-group > .input-group-prepend:not(:first-child) > .input-group-text {
  border-top-left-radius: 0.1875rem;
  border-bottom-left-radius: 0.1875rem;
}
.popover {
  font-family: "Sarabun", sans-serif;
  max-width: 500px;
}
</style>



// WEBPACK FOOTER //
// src/components/tenancy/admin/report/product/ProductCategory.vue